/**
 * KOPS-460: AI Without Fear - Events & Registration Page
 * aisearchportal.com/events
 * Navy + Gold design matching site theme
 */

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt, faMapMarkerAlt, faClock, faUsers,
  faChalkboardTeacher, faTools, faRocket, faCheckCircle,
  faBuilding, faEnvelope, faPhone, faArrowRight,
  faGraduationCap, faShieldAlt, faLaptop, faHome
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';

interface EventData {
  id: string;
  title: string;
  series_name: string;
  category: string;
  description: string;
  event_date: string;
  event_time: string;
  duration_minutes: number;
  location_name: string;
  location_address: string;
  max_capacity: number;
  current_registrations: number;
  sponsor_name: string | null;
  ticket_price: number;
  seats_remaining: number;
  is_full: boolean;
}

const EventsPage: React.FC = () => {
  const [events, setEvents] = useState<EventData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [formData, setFormData] = useState({ fullName: '', email: '', phone: '', brokerage: '', licenseNumber: '', optIn: false });
  const [hostForm, setHostForm] = useState({ contactName: '', companyName: '', companyType: 'Title Company', email: '', phone: '', preferredDates: '', venueAddress: '', expectedAttendees: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<{ success: boolean; message: string } | null>(null);
  const [hostSubmitting, setHostSubmitting] = useState(false);
  const [hostResult, setHostResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showHostForm, setShowHostForm] = useState(false);

  const colors = {
    navy: '#0F1E35', navy2: '#162844', navy3: '#1E3557',
    gold: '#C9952A', gold2: '#E8B44A', gold3: '#F5D080',
    white: '#FFFFFF', off: '#E8EEF6', muted: '#8FA3BF'
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axiosInstance.get('/events/upcoming');
      if (response.data.success) {
        setEvents(response.data.events);
        if (response.data.events.length > 0) {
          setSelectedEventId(response.data.events[0].id);
        }
      }
    } catch (err) {
      console.error('Failed to fetch events:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitResult(null);

    try {
      const response = await axiosInstance.post('/events/register', {
        eventId: selectedEventId,
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        brokerage: formData.brokerage || undefined,
        licenseNumber: formData.licenseNumber || undefined
      });

      setSubmitResult({ success: true, message: response.data.message });
      setFormData({ fullName: '', email: '', phone: '', brokerage: '', licenseNumber: '', optIn: false });
      fetchEvents(); // Refresh seat counts
    } catch (err: any) {
      setSubmitResult({
        success: false,
        message: err.response?.data?.error || 'Registration failed. Please try again.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleHostRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setHostSubmitting(true);
    setHostResult(null);

    try {
      const response = await axiosInstance.post('/events/host-request', {
        contactName: hostForm.contactName,
        companyName: hostForm.companyName,
        companyType: hostForm.companyType,
        email: hostForm.email,
        phone: hostForm.phone,
        preferredDates: hostForm.preferredDates || undefined,
        venueAddress: hostForm.venueAddress || undefined,
        expectedAttendees: hostForm.expectedAttendees ? parseInt(hostForm.expectedAttendees) : undefined,
        notes: hostForm.notes || undefined
      });

      setHostResult({ success: true, message: response.data.message });
      setHostForm({ contactName: '', companyName: '', companyType: 'Title Company', email: '', phone: '', preferredDates: '', venueAddress: '', expectedAttendees: '', notes: '' });
    } catch (err: any) {
      setHostResult({
        success: false,
        message: err.response?.data?.error || 'Request failed. Please try again.'
      });
    } finally {
      setHostSubmitting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    // Append timezone to prevent UTC shifting - dates are stored in Arizona time
    const date = new Date(dateStr.includes('T') && !dateStr.includes('Z') ? dateStr + '-07:00' : dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      timeZone: 'America/Phoenix'
    });
  };

  return (
    <div style={{ background: colors.navy, color: colors.white, fontFamily: "'DM Sans', -apple-system, sans-serif", minHeight: '100vh' }}>
      <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

      {/* Navigation */}
      <nav style={{ background: `linear-gradient(135deg, ${colors.navy3} 0%, #0d1e38 100%)`, borderBottom: `2px solid ${colors.gold}`, padding: '16px 0' }}>
        <Container className="d-flex justify-content-between align-items-center">
          <div>
            <a href="/" style={{ textDecoration: 'none' }}>
              <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', letterSpacing: '2px', color: colors.white }}>
                {typeof window !== 'undefined' && (window.location.hostname.includes('kairosiq.ai')) ? (
                  <>KAIROS<span style={{ color: colors.gold2 }}>IQ</span> <span style={{ color: colors.muted, fontSize: '18px' }}>AI</span></>
                ) : (
                  <>AI<span style={{ color: colors.gold2 }}>Search</span>Portal<span style={{ color: colors.muted, fontSize: '18px' }}>.com</span></>
                )}
              </span>
            </a>
          </div>
          <div className="d-flex gap-3 align-items-center">
            <a href="/pricing" style={{ color: colors.muted, textDecoration: 'none', fontSize: '14px', fontWeight: 600 }}>Pricing</a>
            <a href="/events" style={{ color: colors.gold2, textDecoration: 'none', fontSize: '14px', fontWeight: 600 }}>Events</a>
            <Button variant="outline-light" size="sm" onClick={() => window.location.href = '/agent-portal/login'} style={{ borderColor: colors.gold, color: colors.gold }}>
              Sign In
            </Button>
            <Button size="sm" onClick={() => window.location.href = '/agent-portal/signup'} style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 600 }}>
              Get Started
            </Button>
          </div>
        </Container>
      </nav>

      {/* Hero Section */}
      <section style={{ background: `linear-gradient(180deg, ${colors.navy} 0%, ${colors.navy2} 100%)`, padding: '80px 0 60px', textAlign: 'center' }}>
        <Container>
          <p style={{ color: colors.gold2, fontSize: '14px', fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '16px' }}>
            KairosIQ AI Seminar Series
          </p>
          <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '72px', letterSpacing: '3px', marginBottom: '16px', lineHeight: 1.1, color: colors.white }}>
            AI Without <span style={{ color: colors.gold2 }}>Fear</span>
          </h1>
          <p style={{ fontSize: '22px', color: colors.muted, maxWidth: '700px', margin: '0 auto 24px' }}>
            A 90-Minute Hands-On Seminar for Real Estate Professionals
          </p>
          <p style={{ fontSize: '16px', fontWeight: 300, maxWidth: '650px', margin: '0 auto 32px', lineHeight: 1.7 }}>
            Compliance. Protection. Confidence.
          </p>
          <div style={{ background: colors.navy3, borderLeft: `4px solid ${colors.gold}`, padding: '20px 30px', maxWidth: '700px', margin: '0 auto', borderRadius: '4px', textAlign: 'left' }}>
            <p style={{ fontStyle: 'italic', fontSize: '18px', margin: 0, lineHeight: 1.6 }}>
              "AI is already in your business. The only question is whether you're using it within the law — or creating liability you don't even know about."
            </p>
            <p style={{ color: colors.gold2, margin: '10px 0 0', fontSize: '14px', fontWeight: 600 }}>
              — Michael A. Boykin Jr., M.S. | Author, AI Without Fear
            </p>
          </div>
          <div className="mt-4">
            <Button size="lg" href="#register" style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 700, padding: '14px 40px', fontSize: '18px' }}>
              Register Now <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
            </Button>
          </div>
        </Container>
      </section>

      {/* Upcoming Events */}
      <section style={{ background: colors.navy2, padding: '60px 0' }}>
        <Container>
          <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '42px', textAlign: 'center', marginBottom: '40px', letterSpacing: '2px', color: colors.white }}>
            Upcoming <span style={{ color: colors.gold2 }}>Sessions</span>
          </h2>

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" style={{ color: colors.gold }} /></div>
          ) : events.length === 0 ? (
            <div className="text-center py-5">
              <p style={{ color: colors.muted, fontSize: '18px' }}>New sessions are being scheduled. Check back soon or request to host one below!</p>
            </div>
          ) : (
            <Row className="g-4">
              {events.map(event => (
                <Col md={6} lg={4} key={event.id}>
                  <div style={{ background: colors.navy3, borderRadius: '12px', overflow: 'hidden', borderLeft: `4px solid ${colors.gold}`, height: '100%' }}>
                    <div style={{ padding: '24px' }}>
                      <div style={{ color: colors.gold2, fontSize: '12px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '8px' }}>
                        {event.series_name} {event.category === 'ce_course' ? '| CE Course' : ''}
                      </div>
                      <h3 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '16px', color: colors.white }}>{event.title}</h3>

                      <div style={{ fontSize: '14px', color: colors.muted }}>
                        <p className="mb-2"><FontAwesomeIcon icon={faCalendarAlt} className="me-2" style={{ color: colors.gold2 }} />{formatDate(event.event_date)}</p>
                        <p className="mb-2"><FontAwesomeIcon icon={faClock} className="me-2" style={{ color: colors.gold2 }} />{event.event_time}</p>
                        <p className="mb-2"><FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" style={{ color: colors.gold2 }} />{event.location_name}</p>
                        <p className="mb-2" style={{ fontSize: '13px' }}>{event.location_address}</p>
                        {event.sponsor_name && (
                          <p className="mb-2"><FontAwesomeIcon icon={faBuilding} className="me-2" style={{ color: colors.gold2 }} />Sponsored by {event.sponsor_name}</p>
                        )}
                      </div>

                      <div className="d-flex justify-content-between align-items-center mt-3 pt-3" style={{ borderTop: `1px solid ${colors.navy}` }}>
                        <div>
                          {event.ticket_price > 0 && (
                            <span style={{ fontSize: '18px', color: colors.gold2, fontWeight: 700, display: 'block' }}>
                              ${event.ticket_price}
                            </span>
                          )}
                          <span style={{ fontSize: '12px', color: event.is_full ? '#e74c3c' : '#27ae60', fontWeight: 600 }}>
                            <FontAwesomeIcon icon={faUsers} className="me-1" />
                            {event.is_full ? 'FULL' : `${event.seats_remaining} seats left`}
                          </span>
                        </div>
                        <Button
                          size="sm"
                          disabled={event.is_full}
                          onClick={() => { window.location.href = `/events/${event.id}`; }}
                          style={{ background: event.is_full ? '#555' : `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 600 }}
                        >
                          {event.is_full ? 'Waitlist' : event.ticket_price > 0 ? `Register — $${event.ticket_price}` : 'Register'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )}
        </Container>
      </section>

      {/* Why Attend */}
      <section style={{ background: colors.navy, padding: '60px 0' }}>
        <Container>
          <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '42px', textAlign: 'center', marginBottom: '40px', letterSpacing: '2px', color: colors.white }}>
            Why <span style={{ color: colors.gold2 }}>Attend</span>
          </h2>
          <Row className="g-4">
            {[
              { icon: faShieldAlt, title: 'Protect Your License', desc: 'Understand how AI creates legal risk — Fair Housing violations, advertising liability, and privacy exposure' },
              { icon: faHome, title: 'Built for Real Estate', desc: 'Not generic AI training. Every example comes from real listings, real lawsuits, and real Arizona statutes' },
              { icon: faTools, title: 'Compliance Frameworks', desc: 'Leave with practical checklists and review processes you can implement immediately' },
              { icon: faGraduationCap, title: 'All Experience Levels', desc: "Whether you've never touched AI or use it daily, you need to understand the legal risks" }
            ].map((item, i) => (
              <Col md={3} sm={6} key={i}>
                <div className="text-center" style={{ padding: '20px' }}>
                  <div style={{ background: colors.navy3, width: '60px', height: '60px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                    <FontAwesomeIcon icon={item.icon} style={{ color: colors.gold2, fontSize: '24px' }} />
                  </div>
                  <h4 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '8px', color: colors.white }}>{item.title}</h4>
                  <p style={{ fontSize: '13px', color: colors.muted, margin: 0 }}>{item.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Registration Form */}
      <section id="register" style={{ background: colors.navy2, padding: '60px 0' }}>
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6}>
              <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '42px', textAlign: 'center', marginBottom: '12px', letterSpacing: '2px', color: colors.white }}>
                Reserve Your <span style={{ color: colors.gold2 }}>Seat</span>
              </h2>
              <p style={{ textAlign: 'center', color: colors.muted, marginBottom: '32px' }}>
                Free to attend. Interactive session with real case studies and compliance frameworks.
              </p>

              {submitResult && (
                <Alert variant={submitResult.success ? 'success' : 'danger'} className="mb-4">
                  {submitResult.success && <FontAwesomeIcon icon={faCheckCircle} className="me-2" />}
                  {submitResult.message}
                </Alert>
              )}

              <Form onSubmit={handleRegister} style={{ background: colors.navy3, borderRadius: '12px', padding: '32px', border: `1px solid ${colors.navy}` }}>
                {events.length > 1 && (
                  <Form.Group className="mb-3">
                    <Form.Label style={{ color: colors.muted, fontSize: '13px', fontWeight: 600 }}>Select Event</Form.Label>
                    <Form.Select value={selectedEventId} onChange={e => setSelectedEventId(e.target.value)} style={{ background: colors.navy2, color: colors.white, border: `1px solid ${colors.navy}` }}>
                      {events.map(ev => (
                        <option key={ev.id} value={ev.id} disabled={ev.is_full}>
                          {ev.title} — {formatDate(ev.event_date)} {ev.is_full ? '(FULL)' : `(${ev.seats_remaining} seats)`}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                )}

                <Form.Group className="mb-3">
                  <Form.Label style={{ color: colors.muted, fontSize: '13px', fontWeight: 600 }}>Full Name *</Form.Label>
                  <Form.Control required value={formData.fullName} onChange={e => setFormData({ ...formData, fullName: e.target.value })} placeholder="Jane Smith" style={{ background: colors.navy2, color: colors.white, border: `1px solid ${colors.navy}` }} />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={{ color: colors.muted, fontSize: '13px', fontWeight: 600 }}>Email *</Form.Label>
                  <Form.Control required type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} placeholder="jane@example.com" style={{ background: colors.navy2, color: colors.white, border: `1px solid ${colors.navy}` }} />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={{ color: colors.muted, fontSize: '13px', fontWeight: 600 }}>Phone *</Form.Label>
                  <Form.Control required value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} placeholder="(480) 555-1234" style={{ background: colors.navy2, color: colors.white, border: `1px solid ${colors.navy}` }} />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={{ color: colors.muted, fontSize: '13px', fontWeight: 600 }}>Brokerage <span style={{ color: colors.muted, fontWeight: 400 }}>(optional)</span></Form.Label>
                  <Form.Control value={formData.brokerage} onChange={e => setFormData({ ...formData, brokerage: e.target.value })} placeholder="Your Brokerage" style={{ background: colors.navy2, color: colors.white, border: `1px solid ${colors.navy}` }} />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label style={{ color: colors.muted, fontSize: '13px', fontWeight: 600 }}>License # <span style={{ color: colors.muted, fontWeight: 400 }}>(optional)</span></Form.Label>
                  <Form.Control value={formData.licenseNumber} onChange={e => setFormData({ ...formData, licenseNumber: e.target.value })} placeholder="SA123456789" style={{ background: colors.navy2, color: colors.white, border: `1px solid ${colors.navy}` }} />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Check
                    type="checkbox"
                    id="opt-in-checkbox"
                    checked={formData.optIn}
                    onChange={e => setFormData({ ...formData, optIn: e.target.checked })}
                    label={
                      <span style={{ color: colors.muted, fontSize: '13px' }}>
                        I agree to receive event updates and communications from KairosIQ AI, Inc. You may unsubscribe at any time.
                      </span>
                    }
                  />
                </Form.Group>

                <Button type="submit" disabled={submitting || !selectedEventId || !formData.optIn} className="w-100" size="lg" style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 700, fontSize: '16px' }}>
                  {submitting ? <><Spinner size="sm" className="me-2" />Registering...</> : <>Register Now <FontAwesomeIcon icon={faArrowRight} className="ms-2" /></>}
                </Button>
              </Form>
            </Col>
          </Row>
        </Container>
      </section>

      {/* About the Instructor */}
      <section style={{ background: colors.navy, padding: '60px 0' }}>
        <Container>
          <Row className="align-items-center justify-content-center">
            <Col md={4} className="text-center mb-4 mb-md-0">
              <div style={{ width: '200px', height: '200px', borderRadius: '50%', border: `3px solid ${colors.gold}`, margin: '0 auto', overflow: 'hidden' }}>
                <img src="/images/Boykin_Michael_400.jpg" alt="Michael A. Boykin Jr." style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            </Col>
            <Col md={6}>
              <p style={{ color: colors.gold2, fontSize: '12px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '8px' }}>About the Instructor</p>
              <h3 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '36px', marginBottom: '8px', color: colors.white }}>Michael A. Boykin Jr., M.S.</h3>
              <p style={{ color: colors.gold2, fontSize: '15px', marginBottom: '16px' }}>CEO & Founder, KairosIQ AI | Arizona Licensed Broker</p>
              <p style={{ color: colors.muted, fontSize: '15px', lineHeight: 1.7 }}>
                Author of <em>AI Without Fear</em> (ISBN 978-8-244-44546-6). Michael combines real estate brokerage experience with AI engineering to help agents understand — not fear — the technology transforming their industry.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Host a Session */}
      <section style={{ background: colors.navy, padding: '60px 0' }}>
        <Container>
          <div className="text-center mb-4">
            <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '42px', marginBottom: '12px', letterSpacing: '2px' }}>
              Host a <span style={{ color: colors.gold2 }}>Session</span>
            </h2>
            <p style={{ color: colors.muted, maxWidth: '600px', margin: '0 auto 24px' }}>
              Are you a title company, brokerage, or real estate organization? Bring AI Without Fear to your team. We handle the content — you provide the venue and audience.
            </p>
            {!showHostForm && (
              <Button onClick={() => setShowHostForm(true)} variant="outline-light" size="lg" style={{ borderColor: colors.gold, color: colors.gold, fontWeight: 600 }}>
                <FontAwesomeIcon icon={faBuilding} className="me-2" />Request to Host
              </Button>
            )}
          </div>

          {showHostForm && (
            <Row className="justify-content-center">
              <Col md={8} lg={6}>
                {hostResult && (
                  <Alert variant={hostResult.success ? 'success' : 'danger'} className="mb-4">
                    {hostResult.message}
                  </Alert>
                )}

                <Form onSubmit={handleHostRequest} style={{ background: colors.navy3, borderRadius: '12px', padding: '32px', border: `1px solid ${colors.navy}` }}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: colors.muted, fontSize: '13px', fontWeight: 600 }}>Contact Name *</Form.Label>
                        <Form.Control required value={hostForm.contactName} onChange={e => setHostForm({ ...hostForm, contactName: e.target.value })} style={{ background: colors.navy2, color: colors.white, border: `1px solid ${colors.navy}` }} />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: colors.muted, fontSize: '13px', fontWeight: 600 }}>Company Name *</Form.Label>
                        <Form.Control required value={hostForm.companyName} onChange={e => setHostForm({ ...hostForm, companyName: e.target.value })} style={{ background: colors.navy2, color: colors.white, border: `1px solid ${colors.navy}` }} />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label style={{ color: colors.muted, fontSize: '13px', fontWeight: 600 }}>Company Type *</Form.Label>
                    <Form.Select value={hostForm.companyType} onChange={e => setHostForm({ ...hostForm, companyType: e.target.value })} style={{ background: colors.navy2, color: colors.white, border: `1px solid ${colors.navy}` }}>
                      <option>Title Company</option>
                      <option>Brokerage</option>
                      <option>Real Estate Association</option>
                      <option>Mortgage/Lending</option>
                      <option>Other</option>
                    </Form.Select>
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: colors.muted, fontSize: '13px', fontWeight: 600 }}>Email *</Form.Label>
                        <Form.Control required type="email" value={hostForm.email} onChange={e => setHostForm({ ...hostForm, email: e.target.value })} style={{ background: colors.navy2, color: colors.white, border: `1px solid ${colors.navy}` }} />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: colors.muted, fontSize: '13px', fontWeight: 600 }}>Phone *</Form.Label>
                        <Form.Control required value={hostForm.phone} onChange={e => setHostForm({ ...hostForm, phone: e.target.value })} style={{ background: colors.navy2, color: colors.white, border: `1px solid ${colors.navy}` }} />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label style={{ color: colors.muted, fontSize: '13px', fontWeight: 600 }}>Venue Address</Form.Label>
                    <Form.Control value={hostForm.venueAddress} onChange={e => setHostForm({ ...hostForm, venueAddress: e.target.value })} placeholder="Where would you like to host?" style={{ background: colors.navy2, color: colors.white, border: `1px solid ${colors.navy}` }} />
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: colors.muted, fontSize: '13px', fontWeight: 600 }}>Preferred Dates</Form.Label>
                        <Form.Control value={hostForm.preferredDates} onChange={e => setHostForm({ ...hostForm, preferredDates: e.target.value })} placeholder="e.g., Late April, any Tuesday" style={{ background: colors.navy2, color: colors.white, border: `1px solid ${colors.navy}` }} />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: colors.muted, fontSize: '13px', fontWeight: 600 }}>Expected Attendees</Form.Label>
                        <Form.Control type="number" value={hostForm.expectedAttendees} onChange={e => setHostForm({ ...hostForm, expectedAttendees: e.target.value })} placeholder="20-60" style={{ background: colors.navy2, color: colors.white, border: `1px solid ${colors.navy}` }} />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-4">
                    <Form.Label style={{ color: colors.muted, fontSize: '13px', fontWeight: 600 }}>Additional Notes</Form.Label>
                    <Form.Control as="textarea" rows={3} value={hostForm.notes} onChange={e => setHostForm({ ...hostForm, notes: e.target.value })} style={{ background: colors.navy2, color: colors.white, border: `1px solid ${colors.navy}` }} />
                  </Form.Group>

                  <Button type="submit" disabled={hostSubmitting} className="w-100" size="lg" style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 700 }}>
                    {hostSubmitting ? <><Spinner size="sm" className="me-2" />Submitting...</> : 'Submit Request'}
                  </Button>
                </Form>
              </Col>
            </Row>
          )}
        </Container>
      </section>

      {/* Footer */}
      <footer style={{ background: '#0a1628', padding: '40px 0 20px', borderTop: `1px solid ${colors.navy3}` }}>
        <Container>
          <Row>
            <Col md={6}>
              <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '24px', letterSpacing: '2px' }}>
                {typeof window !== 'undefined' && (window.location.hostname.includes('kairosiq.ai')) ? (
                  <>KAIROS<span style={{ color: colors.gold2 }}>IQ</span> <span style={{ color: colors.muted, fontSize: '14px' }}>AI</span></>
                ) : (
                  <>AI<span style={{ color: colors.gold2 }}>Search</span>Portal<span style={{ color: colors.muted, fontSize: '14px' }}>.com</span></>
                )}
              </span>
              <p style={{ color: colors.muted, fontSize: '13px', marginTop: '8px' }}>
                {typeof window !== 'undefined' && (window.location.hostname.includes('kairosiq.ai')) ? 'KairosIQ AI, Inc. — Chandler, Arizona' : 'Built by Arizona Licensed Brokers. Powered by KairosIQ AI, Inc.'}
              </p>
            </Col>
            <Col md={6} className="text-md-end">
              <p style={{ color: colors.muted, fontSize: '13px' }}>
                KairosIQ AI, Inc. | Chandler, Arizona<br />
                (520) 251-9595 | support@kairosiq.ai
              </p>
            </Col>
          </Row>
          <hr style={{ borderColor: colors.navy3, margin: '20px 0' }} />
          <p style={{ color: colors.muted, fontSize: '11px', textAlign: 'center', margin: 0 }}>
            AI estimates. Agents decide. | "AI Without Fear" is a registered trademark of KairosIQ AI, Inc.
          </p>
        </Container>
      </footer>
    </div>
  );
};

export default EventsPage;
